export enum RoleScope {
  Firm = 'firm',
  Global = 'global',
}

// These are permissions that are not assignable to any roles by non super-admins.
// They typically relate to cross-firm endpoints.
export enum UnassignablePermission {
  DeleteUsers = 'delete:users',
  CreateUsers = 'create:users',
  ReadAdminUserOrganisationMembership = 'read:admin-user-organisation-membership',
  CreateAdminUserOrganisationMembership = 'create:admin-user-organisation-membership',
  DeleteAdminUserOrganisationMembership = 'delete:admin-user-organisation-membership',
  ReadAuth0Organisations = 'read:auth0-organisations',
  CreateTag = 'create:tag',
  EditTag = 'edit:tag',
  DeleteTag = 'delete:tag',
  ReadLinkedPortals = 'read:linked-portals',
  CreateLinkedPortals = 'create:linked-portals',
  DeleteLinkedPortals = 'delete:linked-portals',
  ExecuteScripts = 'execute:scripts',
  ReadCollectionHistory = 'read:collection-history',
  CreateFirm = 'create:firm',
  DeleteFirm = 'delete:firm',
  ReadFurtherUser = 'read:further-user',
  DeleteFurtherUser = 'delete:further-user',
  ReadFurtherUserPasswordResetLink = 'read:further-user-password-reset-link',
  LpAllVehiclesMember = 'lp:all-vehicles-member',
}

export enum LpPermission {
  ReadGl = 'read:gl',
  LpCreateAccount = 'lp:create:account',
  LpCreateBankAccount = 'lp:create:bank-account',
  LpCreateBankAccountOperation = 'lp:create:bank-account-operation',
  LpCreateCapitalAction = 'lp:create:capital-action',
  LpCreateEqualisation = 'lp:create:equalisation',
  LpCreateIncomeAndExpenses = 'lp:create:income-and-expenses',
  LpCreateInvestment = 'lp:create:investment',
  LpCreateRealisation = 'lp:create:realisation',
  LpCreateTransactionType = 'lp:create:transaction-type',
  LpCreateUnrealisedAssetFxGainOrLoss = 'lp:create:unrealised-asset-fx-gain-or-loss',
  LpCreateUnrealisedCashFxGainOrLoss = 'lp:create:unrealised-cash-fx-gain-or-loss',
  LpCreateUnrealisedFxGainOrLoss = 'lp:create:unrealised-fx-gain-or-loss',
  LpCreateVehicle = 'lp:create:vehicle',
  LpDeleteAccount = 'lp:delete:account',
  LpDeleteBankAccountOperation = 'lp:delete:bank-account-operation',
  LpDeleteIncomeAndExpenses = 'lp:delete:income-and-expenses',
  LpDeleteInvestment = 'lp:delete:investment',
  LpDeleteRealisation = 'lp:delete:realisation',
  LpDeleteTransactionType = 'lp:delete:transaction-type',
  LpDeleteUnrealisedAssetFxGainOrLoss = 'lp:delete:unrealised-asset-fx-gain-or-loss',
  LpDeleteUnrealisedCashFxGainOrLoss = 'lp:delete:unrealised-cash-fx-gain-or-loss',
  LpDeleteUnrealisedFxGainOrLoss = 'lp:delete:unrealised-fx-gain-or-loss',
  LpEditAccount = 'lp:edit:account',
  LpEditBankAccount = 'lp:edit:bank-account',
  LpEditBankAccountOperation = 'lp:edit:bank-account-operation',
  LpEditCapitalAction = 'lp:edit:capital-action',
  LpEditCapitalActionNotice = 'lp:edit:capital-action-notice',
  LpDeleteCapitalAction = 'lp:delete:capital-action',
  LpDeleteCapitalActionNotice = 'lp:delete:capital-action-notice',
  LpEditCommitment = 'lp:edit:commitment',
  LpDeleteCommitment = 'lp:delete:commitment',
  LpEditFee = 'lp:edit:fee',
  LpEditIncomeAndExpenses = 'lp:edit:income-and-expenses',
  LpEditTransactionType = 'lp:edit:transaction-type',
  LpEditVehicle = 'lp:edit:vehicle',
  LpReadAccounts = 'lp:read:accounts',
  LpReadActions = 'lp:read:actions',
  LpReadAssets = 'lp:read:assets',
  LpReadBankAccount = 'lp:read:bank-account',
  LpReadBankFeed = 'lp:read:bank-feed',
  LpReadCapitalAction = 'lp:read:capital-action',
  LpReadCapitalActionNotice = 'lp:read:capital-action-notice',
  LpReadCommitment = 'lp:read:commitment',
  LpReadContacts = 'lp:read:contacts',
  LpReadEqualisations = 'lp:read:equalisations',
  LpReadFee = 'lp:read:fee',
  LpReadFeeTemplate = 'lp:read:fee-template',
  LpReadHoldingClasses = 'lp:read:holding-classes',
  LpReadIncomeAndExpenses = 'lp:read:income-and-expenses',
  LpReadInvestment = 'lp:read:investment',
  LpReadRealisation = 'lp:read:realisation',
  LpReadUnrealisedAssetFxGainOrLoss = 'lp:read:unrealised-asset-fx-gain-or-loss',
  LpReadUnrealisedCashFxGainOrLoss = 'lp:read:unrealised-cash-fx-gain-or-loss',
  LpReadUnrealisedFxGainOrLoss = 'lp:read:unrealised-fx-gain-or-loss',
  LpReadVehicles = 'lp:read:vehicles',
  LpWriteAssets = 'lp:write:assets',
  LpWriteHoldingClasses = 'lp:write:holding-classes',
  LpCreatePartnerTransfer = 'lp:create:partner-transfer',
  LpReadPrepayments = 'lp:read:prepayments',
  LpReadAccruals = 'lp:read:accruals',
  LpCreatePrepayments = 'lp:create:prepayments',
  LpReadManualJournal = 'lp:read:manual-journal',
  LpCreateManualAction = 'lp:create:manual-action',
  LpDeleteManualAction = 'lp:delete:manual-action',
  LpEditManualAction = 'lp:edit:manual-action',
}

export enum CommonPermission {
  // admin user roles
  CreateRole = 'create:role',
  ReadRole = 'read:role',
  UpdateRole = 'update:role',
  DeleteRole = 'delete:role',
  // admin user management
  ReadUsers = 'read:users',
  EditUsers = 'edit:users',
  RemoveUserFromFirm = 'delete:users:current-firm',
  AddUserToFirm = 'create:users:current-firm',
  // impersonation access
  UseImpersonationAccessInvestor = 'use:impersonation-access:investor',
  // firms (both LP and EIS accordingly)
  ReadFirm = 'read:firm',
  EditFirm = 'edit:firm',
  // investors (LP and EIS shared collection)
  CreateInvestor = 'create:investor',
  ReadInvestor = 'read:investor',
  EditInvestor = 'edit:investor',
  DeleteInvestor = 'delete:investor',
}

export enum EisPermission {
  CreateAdvisers = 'create:advisers',
  ReadAdvisers = 'read:advisers',
  EditAdvisers = 'edit:advisers',
  DeleteAdvisers = 'delete:advisers',
  CreateAllocationUpload = 'create:allocationupload',
  ViewAllocationUpload = 'view:allocationupload',
  EditAllocationUpload = 'edit:allocationupload',
  DeleteAllocationUpload = 'delete:allocationupload',
  CreateBackgroundExport = 'create:background-export',
  ReadBackgroundExport = 'read:background-export',
  DeleteBackgroundExport = 'delete:background-export',
  CreateCompanies = 'create:companies',
  ReadCompanies = 'read:companies',
  EditCompanies = 'edit:companies',
  DeleteCompanies = 'delete:companies',
  CreateCoreFees = 'create:corefees',
  ReadCoreFees = 'read:corefees',
  DeleteCoreFees = 'delete:corefees',
  CreateExit = 'create:exit',
  ReadExit = 'read:exit',
  DeleteExit = 'delete:exit',
  ReadInterest = 'read:interest',
  DeleteInterest = 'delete:interest',
  UploadInterest = 'upload:interest',
  CreateInvestment = 'create:investment',
  ReadInvestment = 'read:investment',
  EditInvestment = 'edit:investment',
  DeleteInvestment = 'delete:investment',
  CreateInvestorAccessGrant = 'create:investor-access-grant',
  ReadInvestorAccessGrant = 'read:investor-access-grant',
  EditInvestorAccessGrant = 'edit:investor-access-grant',
  DeleteInvestorAccessGrant = 'delete:investor-access-grant',
  DeleteInvestorCertification = 'delete:investor-certification',
  ReadInvestorKyc = 'read:investor-kyc',
  EditInvestorKyc = 'edit:investor-kyc',
  CreateOrganisationUsers = 'create:organisation-users',
  ReadOrganisationUsers = 'read:organisation-users',
  DeleteOrganisationUsers = 'delete:organisation-users',
  CreateOrganisations = 'create:organisations',
  ReadOrganisations = 'read:organisations',
  EditOrganisations = 'edit:organisations',
  DeleteOrganisations = 'delete:organisations',
  ReadPaymentExpectations = 'read:payment-expectations',
  CreatePerformanceFees = 'create:performancefees',
  ReadPerformanceFees = 'read:performancefees',
  DeletePerformanceFees = 'delete:performancefees',
  CreateReport = 'create:report',
  EditReport = 'edit:report',
  DeleteReport = 'delete:report',
  EditInvestorBulletin = 'edit:investor-bulletin',
  EditTaxReport = 'edit:tax-report',
  CreateSeis = 'create:seis',
  ReadSeis = 'read:seis',
  EditSeis = 'edit:seis',
  DeleteSeis = 'delete:seis',
  DownloadSeis = 'download:seis',
  CreateShareSplit = 'create:share-split',
  ReadShareSplit = 'read:share-split',
  CreateTranche = 'create:tranche',
  ReadTranche = 'read:tranche',
  EditTranche = 'edit:tranche',
  DeleteTranche = 'delete:tranche',
  DuplicateTranche = 'duplicate:tranche',
  DeleteTransfer = 'delete:transfer',
  ReadTransfers = 'read:transfers',
  TransferInvestment = 'transfer:investment',
  ReadWithdrawals = 'read:withdrawals',
  RequestWithdrawals = 'request:withdrawals',
  UseImpersonationAccessAdviser = 'use:impersonation-access:adviser',
  UseImpersonationAccessOrgUser = 'use:impersonation-access:org-user',
  EditFurtherUser = 'edit:further-user',
  ReadTag = 'read:tag',
  ExportCustodianFees = 'export:custodian-fees',
  ExportCustodianWithdrawals = 'export:custodian-withdrawals',
  CreateOneOffFeesAndDiscounts = 'create:one-off-fees-and-discounts',
  DeleteOneOffFeesAndDiscounts = 'delete:one-off-fees-and-discounts',
  UploadInvestor = 'upload:investor',
}

export const RolePermission = {
  ...UnassignablePermission,
  ...CommonPermission,
  ...EisPermission,
  ...LpPermission,
};

export type RolePermission =
  | UnassignablePermission
  | CommonPermission
  | EisPermission
  | LpPermission;
